/**
 * PolicyIntel Explore — v2.0 unified research entry.
 * Issues, sectors, observed movers and the existing professional screener.
 * All numbers come from server responses; missing evidence stays unknown.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ScreenerPage from './ScreenerPage';
import { trackUxEvent } from '../analytics/analytics';

type View = 'ISSUES' | 'SECTORS' | 'MOVERS' | 'SCREENER';
type Obj = Record<string, unknown>;
type Status = 'idle' | 'loading' | 'success' | 'error';
type Mover = { ticker: string; name: string; pct: number | null; foreignIntensity: number | null; flowStatus: string };
type Sector = { key: string; name: string; change: number | null; up: number | null; down: number | null; total: number | null; observedAt: string };
type PublicEvent = { headline: string; observedAt: string; status: string; source: string; url: string };
type Brief = { status: string; sessionDate: string; generatedAt: string; movers: Mover[]; event: PublicEvent | null };

const NAV: Array<{ id: View; title: string; description: string; icon: React.ReactNode }> = [
  {id:'ISSUES',title:'이슈',description:'확인된 이벤트와 원문',icon:<ArticleRoundedIcon fontSize="small" />},
  {id:'SECTORS',title:'업종',description:'상승·하락과 확산 정도',icon:<CategoryRoundedIcon fontSize="small" />},
  {id:'MOVERS',title:'움직임',description:'가격 변화와 수급의 관계',icon:<TimelineRoundedIcon fontSize="small" />},
  {id:'SCREENER',title:'스크리너',description:'조건으로 직접 종목 찾기',icon:<TuneRoundedIcon fontSize="small" />},
];
const COLORS = {
  canvas:'#F6F8FB',surface:'#FFFFFF',navy:'#10263F',ink:'#122033',muted:'#607086',
  blue:'#2F6BFF',line:'#E2E8F0',risk:'#D84C57',stable:'#178A52',unknown:'#77869A',
};
const obj = (value: unknown): Obj => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Obj : {};
const str = (value: unknown): string => value === undefined || value === null ? '' : String(value).trim();
const finite = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};
const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];
const field = (row: Obj, ...keys: string[]): unknown => {
  for (const key of keys) if (row[key] !== undefined && row[key] !== null) return row[key];
  return undefined;
};
const ticker = (value: unknown): string => str(value).match(/^A?(\d{6})$/)?.[1] ?? '';
const koreanDate = (value: string): string => {
  const ms = Date.parse(value);
  return Number.isFinite(ms)
    ? new Intl.DateTimeFormat('ko-KR',{timeZone:'Asia/Seoul',dateStyle:'short',timeStyle:'short'}).format(new Date(ms))
    : (value || '기준 시각 확인 필요');
};
const pct = (value: number | null): string => value === null ? '자료 없음' : `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
const colorOf = (v: number | null): string => v === null || v === 0 ? COLORS.muted : v > 0 ? COLORS.stable : COLORS.risk;

function normalizeBrief(value: unknown): Brief {
  const data = obj(value);
  const event = obj(data.event);
  const normalizedEvent: PublicEvent | null = str(event.status) === 'EDITOR_VERIFIED_PUBLIC_LINK' && !!str(event.headline)
    ? { headline:str(event.headline), observedAt:str(event.observedAt),status:str(event.status),source:str(event.source),url:str(event.url) }
    : null;
  const movers = asArray(data.movers).map(rowValue => {
    const row = obj(rowValue);
    const id = ticker(row.ticker);
    if (!id) return null;
    return {ticker:id,name:str(row.name)||id,pct:finite(row.priceChangePct),
      foreignIntensity:finite(row.foreignIntensityPct),flowStatus:str(row.flowStatus)};
  }).filter((entry): entry is Mover => entry !== null);
  return { status:str(data.status),sessionDate:str(data.sessionDate),generatedAt:str(data.generatedAt),movers,event:normalizedEvent };
}
function normalizeSectorPayload(value: unknown): Sector[] {
  const root = obj(value);
  const wrapped = obj(root.data);
  const candidates: unknown[] = Array.isArray(value) ? value
    : Array.isArray(root.sectors) ? root.sectors
    : Array.isArray(root.items) ? root.items
    : Array.isArray(root.rows) ? root.rows
    : Array.isArray(root.sectorRows) ? root.sectorRows
    : Array.isArray(root.data) ? root.data
    : Array.isArray(wrapped.sectors) ? wrapped.sectors
    : Array.isArray(wrapped.items) ? wrapped.items : [];
  const found = new Map<string,Sector>();
  for (const item of candidates) {
    const row = obj(item);
    const name = str(field(row,'sectorName','sector_name','name','industryName','industry_name'));
    if (!name) continue;
    const key = str(field(row,'sectorCode','sector_code','code')) || name;
    if (found.has(key)) continue;
    const up = finite(field(row,'advanceCount','advancingCount','upCount','risingCount'));
    const down = finite(field(row,'declineCount','decliningCount','downCount','fallingCount'));
    const total = finite(field(row,'stockCount','totalCount','constituentCount'));
    found.set(key, {key,name,change:finite(field(row,'changeRate','changeRatePct','changePct','avgChangeRate')),
      up,down,total,observedAt:str(field(row,'observedAt','updatedAt','sessionDate'))});
  }
  return Array.from(found.values()).sort((a,b) => (b.change ?? -Infinity) - (a.change ?? -Infinity));
}

const panelSx = { bgcolor:COLORS.surface,border:`1px solid ${COLORS.line}`,borderRadius:'16px',p:{xs:2,md:3} } as const;

export default function ExplorePage({onGoStocks}: {onGoStocks?: (category: string,ticker?: string) => void}) {
  const navigate = useNavigate();
  const [searchParams,setSearchParams] = useSearchParams();
  const rawView = str(searchParams.get('view')).toUpperCase();
  const currentView: View = NAV.some(item => item.id === rawView) ? rawView as View : 'ISSUES';
  const [brief,setBrief] = useState<Brief | null>(null);
  const [briefStatus,setBriefStatus] = useState<Status>('idle');
  const [briefError,setBriefError] = useState('');
  const [sectorRows,setSectorRows] = useState<Sector[]>([]);
  const [sectorStatus,setSectorStatus] = useState<Status>('idle');
  const [sectorError,setSectorError] = useState('');
  const [refresh,setRefresh] = useState(0);
  useEffect(() => {
    trackUxEvent('explore_view',{screen_name:'explore',view:currentView.toLowerCase()});
  },[currentView]);
  const chooseView = useCallback((next: View) => {
    const params = new URLSearchParams(searchParams);
    params.set('view',next);
    setSearchParams(params);
    trackUxEvent('explore_tab_select',{screen_name:'explore',view:next.toLowerCase()});
  },[searchParams,setSearchParams]);

  useEffect(() => {
    if (currentView === 'SCREENER') return;
    const controller = new AbortController();
    setBriefStatus('loading');setBriefError('');
    fetch('/api/main/market-intelligence/brief/latest',{
      credentials:'include',headers:{Accept:'application/json'},signal:controller.signal,
    })
      .then(async response => {if(!response.ok)throw new Error(`HTTP ${response.status}`);return response.json() as Promise<unknown>;})
      .then(payload => {if(controller.signal.aborted)return;setBrief(normalizeBrief(payload));setBriefStatus('success');})
      .catch(error => {if(controller.signal.aborted)return;setBrief(null);setBriefError(error instanceof Error ? error.message : '조회 오류');setBriefStatus('error');});
    return () => controller.abort();
  },[currentView,refresh]);

  useEffect(() => {
    if(currentView !== 'SECTORS')return;
    const controller = new AbortController();
    setSectorStatus('loading');setSectorError('');
    fetch('/api/main/sector-dashboard',{
      credentials:'include',headers:{Accept:'application/json'},signal:controller.signal,
    })
      .then(async response => {if(!response.ok)throw new Error(`HTTP ${response.status}`);return response.json() as Promise<unknown>;})
      .then(payload => {if(controller.signal.aborted)return;setSectorRows(normalizeSectorPayload(payload));setSectorStatus('success');})
      .catch(error => {if(controller.signal.aborted)return;setSectorRows([]);setSectorError(error instanceof Error ? error.message : '조회 오류');setSectorStatus('error');});
    return () => controller.abort();
  },[currentView,refresh]);

  const movers = useMemo(() => (brief?.status === 'OK' ? brief.movers : [])
    .filter(item => item.pct !== null)
    .sort((a,b) => Math.abs(b.pct ?? 0) - Math.abs(a.pct ?? 0)),[brief]);

  const openStock = useCallback((code: string, from: string) => {
    trackUxEvent('explore_stock_open',{screen_name:'explore',stock_code:code,source:from});
    // /stock/:ticker is the v2 decision workspace; fallback remains on /stocks.
    navigate(`/stock/${code}`);
  },[navigate]);
  const go = useCallback((path: string,source: string) => {
    trackUxEvent('explore_tool_open',{screen_name:'explore',source});navigate(path);
  },[navigate]);
  const openScreenerResult = useCallback((category: string, stockCode?: string) => {
    const code = ticker(stockCode);
    if (code) { openStock(code,'explore-screener'); return; }
    // Keep the original adapter for tools that do not supply a ticker.
    onGoStocks?.(category,stockCode);
  },[onGoStocks,openStock]);

  return <Box sx={{bgcolor:COLORS.canvas,color:COLORS.ink,fontFamily:'Pretendard, system-ui, sans-serif',mx:{xs:-1.5,md:0},minHeight:'75vh',pb:6}}>
    <Box sx={{maxWidth:1380,mx:'auto',px:{xs:2,md:4},pt:{xs:3,md:5}}}>
      <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" alignItems={{xs:'stretch',sm:'flex-end'}} spacing={2}>
        <Box>
          <Typography sx={{fontSize:11.5,fontWeight:850,color:COLORS.blue,letterSpacing:'.12em'}}>POLICYINTEL / EXPLORE</Typography>
          <Typography component="h1" sx={{fontSize:{xs:27,md:34},fontWeight:860,letterSpacing:'-.045em',mt:1}}>무슨 변화가 있었고, 어디를 확인해야 할까요?</Typography>
          <Typography sx={{mt:1,color:COLORS.muted,fontSize:{xs:13.5,md:14.5},lineHeight:1.8}}>
            이슈·업종·종목 움직임을 나눠 확인하고, 조건에 맞는 종목은 스크리너에서 찾아보세요.
          </Typography>
        </Box>
        <Button size="small" startIcon={<RefreshRoundedIcon/>} onClick={()=>setRefresh(i=>i+1)}
          sx={{alignSelf:{xs:'flex-start',sm:'flex-end'},border:'1px solid #D8E3F0',borderRadius:2,color:COLORS.navy,fontWeight:750,px:1.6}}
          disabled={briefStatus==='loading'||sectorStatus==='loading'}>데이터 새로 확인</Button>
      </Stack>

      <Box role="tablist" aria-label="탐색 유형" sx={{mt:3,display:'grid',gridTemplateColumns:{xs:'repeat(2,minmax(0,1fr))',md:'repeat(4,minmax(0,1fr))'},gap:1.4}}>
        {NAV.map(item => {
          const selected=item.id===currentView;
          return <Button key={item.id} role="tab" aria-selected={selected} aria-controls="explore-results"
            onClick={()=>chooseView(item.id)} startIcon={item.icon}
            sx={{bgcolor:selected?COLORS.navy:COLORS.surface,color:selected?'#fff':COLORS.ink,
              border:`1px solid ${selected?COLORS.navy:COLORS.line}`,borderRadius:3,
              minHeight:{xs:70,md:83},px:2,textAlign:'left',justifyContent:'flex-start',
              boxShadow:'none',textTransform:'none','&:hover':{bgcolor:selected?'#173958':'#EFF5FC'},
              '& .MuiButton-startIcon':{mr:1.3,alignSelf:'center'}}}>
              <Box sx={{display:'flex',flexDirection:'column',alignItems:'flex-start',minWidth:0}}>
                <Typography sx={{fontSize:{xs:14,md:15},fontWeight:820}}>{item.title}</Typography>
                <Typography sx={{fontSize:11,opacity:selected ? 0.76 : 0.7,display:{xs:'none',sm:'block'},whiteSpace:'normal'}}>{item.description}</Typography>
              </Box>
            </Button>;
        })}
      </Box>

      <Box id="explore-results" role="tabpanel" sx={{mt:3}}>
      {currentView==='SCREENER' ? <Box sx={{'& .pi-route-container':{px:0}}}>
        <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" alignItems={{xs:'stretch',sm:'center'}} sx={{...panelSx,mb:2}} spacing={1}>
          <Box><Typography sx={{fontWeight:830,fontSize:17}}>조건을 직접 설정해 후보를 찾습니다</Typography>
            <Typography sx={{color:COLORS.muted,fontSize:13,mt:.7}}>기존 전문 스크리너의 재무·수급·기술 필터와 정렬 방식을 그대로 사용합니다.</Typography></Box>
          <Button onClick={()=>go('/screener','screener-full-view')} endIcon={<ArrowForwardRoundedIcon/>} sx={{fontWeight:780}}>전문 도구 전체 화면</Button>
        </Stack>
        <ScreenerPage onGoStocks={openScreenerResult}/>
      </Box> : <>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{mb:1.5}} spacing={2}>
          <Box>
            <Typography component="h2" sx={{fontWeight:850,fontSize:{xs:20,md:23},letterSpacing:'-.025em'}}>
              {currentView==='ISSUES'?'오늘 확인 가능한 이벤트':currentView==='SECTORS'?'업종별 움직임': '움직임 확인이 필요한 종목'}
            </Typography>
            <Typography sx={{color:COLORS.muted,fontSize:12.5,mt:.5}}>
              {brief?.sessionDate ? `시장 브리프 기준 거래일 ${brief.sessionDate}` : '데이터 기준일 확인 중'}
            </Typography>
          </Box>
          {(briefStatus==='loading'||(currentView==='SECTORS'&&sectorStatus==='loading'))&&<CircularProgress size={21}/>}
        </Stack>
        {briefStatus==='error'&&<Alert severity="warning" sx={{mb:2}}>시장 브리프를 조회하지 못했습니다. ({briefError})</Alert>}
        {currentView==='ISSUES'&& <Box sx={panelSx}>
          {briefStatus==='loading'&&<Typography sx={{color:COLORS.muted}}>검증된 이슈를 조회하고 있습니다.</Typography>}
          {briefStatus==='success'&& (brief?.event ? <>
            <Chip size="small" label="원문 출처 확인" sx={{bgcolor:'#EEF4FF',color:'#275BC4',fontWeight:800,mb:2}}/>
            <Typography sx={{fontSize:{xs:18,md:23},fontWeight:850,lineHeight:1.5}}>{brief.event.headline}</Typography>
            <Typography sx={{color:COLORS.muted,mt:1,fontSize:13}}>{brief.event.source || '출처 표시 없음'} · {koreanDate(brief.event.observedAt)}</Typography>
            <Typography sx={{mt:2,color:COLORS.muted,fontSize:13.5,lineHeight:1.8}}>원문 링크의 존재가 주가 변동 원인이나 기업가치 영향을 확정하지는 않습니다. 관련 영향은 종목의 근거 화면에서 별도로 확인해야 합니다.</Typography>
            <Stack direction="row" spacing={1.4} sx={{mt:2,flexWrap:'wrap',rowGap:1}}>
              {!!brief.event.url&&/^https?:\/\//i.test(brief.event.url)&&<Button href={brief.event.url} target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<ArrowForwardRoundedIcon/>}>원문 확인</Button>}
              <Button variant="contained" onClick={()=>go('/issue','issue-feed')} sx={{bgcolor:COLORS.blue,boxShadow:'none'}}>이벤트 전체 보기</Button>
            </Stack>
          </> : <>
            <Typography sx={{fontWeight:800}}>공개 기준으로 확인된 대표 이벤트가 없습니다.</Typography>
            <Typography sx={{color:COLORS.muted,mt:1,fontSize:13.5,lineHeight:1.75}}>뉴스가 없다는 뜻이 아닙니다. 현재 공개 브리프에 원문 출처가 검증된 이벤트가 제공되지 않았습니다.</Typography>
            <Button sx={{mt:2}} onClick={()=>go('/issue','issue-feed')} endIcon={<ArrowForwardRoundedIcon/>}>전체 이벤트 확인</Button>
          </>)}
          <Divider sx={{my:2.5}}/>
          <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" alignItems={{xs:'flex-start',sm:'center'}} spacing={1}>
            <Typography sx={{color:COLORS.muted,fontSize:12.5}}>공시·이벤트는 판단 근거의 일부입니다. 확인되지 않은 인과는 확정하지 않습니다.</Typography>
            <Button onClick={()=>go('/feeds','policy-feed')} size="small" endIcon={<ArrowForwardRoundedIcon/>}>관련 정보 더 보기</Button>
          </Stack>
        </Box>}

        {currentView==='SECTORS'&&<>
          {sectorStatus==='error'&&<Alert severity="warning" sx={{mb:2}}>업종 데이터를 조회하지 못했습니다. ({sectorError})</Alert>}
          {sectorStatus==='success'&&sectorRows.length===0&&<Box sx={panelSx}>
            <Typography sx={{fontWeight:780}}>업종별 확산 데이터를 확인할 수 없습니다.</Typography>
            <Typography sx={{color:COLORS.muted,mt:1,fontSize:13.5}}>현재 응답에서 업종명·등락률·상승/하락 종목 수를 확인할 수 없습니다. 전체 시장 등락을 업종별 수치로 대체하지 않습니다.</Typography>
            <Button onClick={()=>go('/screener','sector-fallback-screener')} sx={{mt:2}} endIcon={<ArrowForwardRoundedIcon/>}>업종 조건으로 종목 찾기</Button>
          </Box>}
          {sectorRows.length>0&&<Box sx={{...panelSx,p:{xs:1.5,md:2.5}}}>
            <Box sx={{display:'grid',gridTemplateColumns:{xs:'minmax(0,1fr) auto',md:'minmax(180px,1fr) 140px 170px'},gap:1.5,px:1.5,pb:1,borderBottom:`1px solid ${COLORS.line}`}}>
              <Typography sx={{fontSize:12,color:COLORS.muted,fontWeight:760}}>업종</Typography>
              <Typography sx={{fontSize:12,color:COLORS.muted,fontWeight:760,textAlign:'right'}}>등락</Typography>
              <Typography sx={{fontSize:12,color:COLORS.muted,fontWeight:760,textAlign:'right',display:{xs:'none',md:'block'}}}>상승 / 하락</Typography>
            </Box>
            {sectorRows.slice(0,30).map(row=><Box key={row.key} sx={{display:'grid',gridTemplateColumns:{xs:'minmax(0,1fr) auto',md:'minmax(180px,1fr) 140px 170px'},gap:1.5,alignItems:'center',px:1.5,py:1.75,borderBottom:`1px solid #EEF1F5`}}>
              <Box><Typography sx={{fontSize:14.5,fontWeight:820}}>{row.name}</Typography>
                {row.observedAt&&<Typography sx={{fontSize:11.5,color:COLORS.muted,mt:.4}}>{koreanDate(row.observedAt)} 기준</Typography>}</Box>
              <Typography sx={{fontSize:14.5,fontWeight:850,color:colorOf(row.change),textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{pct(row.change)}</Typography>
              <Typography sx={{fontSize:13,textAlign:'right',color:COLORS.muted,display:{xs:'none',md:'block'}}}>
                {row.up!==null && row.down!==null ? `${row.up}↑ / ${row.down}↓${row.total!==null?` · 전체 ${row.total}`:''}` : '확산 정보 없음'}
              </Typography>
            </Box>)}
            <Typography sx={{fontSize:12,color:COLORS.muted,mt:2,lineHeight:1.7,px:1}}>업종 등락과 실제 상승 종목 수는 서로 다른 지표입니다. 둘 중 하나라도 없으면 임의로 추정하지 않습니다.</Typography>
          </Box>}
        </>}

        {currentView==='MOVERS'&&<>
          {briefStatus==='success'&&movers.length===0&&<Box sx={panelSx}>
            <Typography sx={{fontWeight:830}}>현재 브리프에 종목별 움직임 자료가 없습니다.</Typography>
            <Typography sx={{color:COLORS.muted,mt:1,fontSize:13.5}}>집계 결과가 없거나 데이터 품질 조건에 부합하지 않을 수 있습니다.</Typography>
            <Button onClick={()=>go('/screener','mover-fallback-screener')} sx={{mt:2}} endIcon={<ArrowForwardRoundedIcon/>}>전문 스크리너에서 찾기</Button>
          </Box>}
          {movers.length>0&&<Box sx={{...panelSx,p:{xs:1.5,md:2.5}}}>
            <Typography sx={{fontSize:12.5,color:COLORS.muted,px:1.5,mb:1.3,lineHeight:1.8}}>공개 브리프의 관측 대상만 표시합니다. 수급 정렬은 잠정 상태일 수 있으며 가격 변동의 원인으로 확정하지 않습니다.</Typography>
            {movers.slice(0,24).map(row=><Button key={row.ticker} onClick={()=>openStock(row.ticker,'movers')} fullWidth
              sx={{display:'grid',gridTemplateColumns:{xs:'minmax(0,1fr) auto',sm:'minmax(0,1fr) 120px 180px 22px'},columnGap:1.2,alignItems:'center',justifyItems:'stretch',textAlign:'left',px:{xs:1.5,md:2},py:2,borderRadius:2,color:COLORS.ink,borderBottom:'1px solid #EDF2F7',textTransform:'none','&:hover':{bgcolor:'#F2F6FC'}}}>
              <Box sx={{minWidth:0}}><Typography sx={{fontWeight:820,fontSize:15,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{row.name}</Typography><Typography sx={{color:COLORS.muted,fontSize:11.5}}>{row.ticker}</Typography></Box>
              <Typography sx={{color:colorOf(row.pct),fontSize:15,fontWeight:860,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{pct(row.pct)}</Typography>
              <Box sx={{display:{xs:'none',sm:'block'},textAlign:'right'}}>
                <Typography sx={{fontSize:12,color:COLORS.muted}}>{row.foreignIntensity===null?'외국인 수급 확인 필요':`외국인 강도 ${pct(row.foreignIntensity)}`}</Typography>
                <Typography sx={{fontSize:11,color:COLORS.unknown}}>{row.flowStatus==='ALIGNED_PROVISIONAL'?'수급 정렬 잠정':'수급 관계 확인 중'}</Typography>
              </Box>
              <ArrowForwardRoundedIcon sx={{fontSize:17,color:COLORS.muted,display:{xs:'none',sm:'block'}}}/>
            </Button>)}
          </Box>}
        </>}
      </>}
      </Box>
      <Box sx={{mt:3,pt:2,borderTop:`1px solid ${COLORS.line}`}}>
        <Typography sx={{fontSize:12.5,lineHeight:1.85,color:COLORS.muted}}>
          탐색 화면은 확인된 데이터만 제공합니다. 종목 상세의 현재 판단·위험·변화·재무 등은 해당 종목 Workspace에서 확인하세요.
          이 화면의 등락과 지표는 투자 권유나 수익 보장이 아닙니다.
        </Typography>
      </Box>
    </Box>
  </Box>;
}
